// eslint-disable-next-line @tseslint/no-shadow
import { ChangeDetectionStrategy, Component, EnvironmentInjector, EventEmitter, inject, input, InputSignal, model, ModelSignal, Output, signal, Signal, viewChild, WritableSignal } from '@angular/core';
import { SideMenuComponent } from "@shared/components/side-menu/side-menu.component";
import { PROJECT_CUSTOMER_DOCUMENT_SPECIAL_DURATIONS, ProjectCustomerDocument, ProjectCustomerDocumentDuration, ProjectCustomerDocumentId } from '../../types/project-customer-document.type';
import { SideMenuTitleComponent } from "@shared/components/side-menu/components/side-menu-title/side-menu-title.component";
import { SideMenuContentComponent } from "@shared/components/side-menu/components/side-menu-content/side-menu-content.component";
import { SideMenuGroupComponent } from "@shared/components/side-menu/components/side-menu-group/side-menu-group.component";
import { SideMenuGroupTitleComponent } from "@shared/components/side-menu/components/side-menu-group/components/side-menu-group-header/side-menu-group-title.component";
import { SideMenuGroupContentComponent } from "@shared/components/side-menu/components/side-menu-group/components/side-menu-group-content/side-menu-group-content.component";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from '@angular/material/input';
import { provideTranslationScope, TranslationPipeModule, TranslationService } from '@ogs-gmbh/ngx-translate';
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatSelectModule } from '@angular/material/select';
import { ProjectCustomerDocumentService } from '../../services/project-customer.service';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter, map, of, startWith, switchMap, tap } from 'rxjs';
import { FormBuilder, FormGroupDirective, ReactiveFormsModule, Validators } from '@angular/forms';
import { isNull, isString } from 'es-toolkit';
import { isDuration } from '@shared/types/duration.type';
import { getLocalizedDuration } from '@shared/functions/get-localized-duration';
import { TextFieldModule } from '@angular/cdk/text-field';
import { FormOf } from '@shared/types/form-of.type';
import { MatButton } from "@angular/material/button";
import { matchesTerm } from '@shared/components/table-implementation/functions/matches-term';
import { InlineConfirmationTriggerDirective } from "@shared/components/inline-confirmation/directives/inline-confirmation-trigger.directive";
import { DeleteButtonComponent } from '@shared/components/buttons/delete-button/delete-button.component';
import { InlineDeleteConfirmationComponent } from '@shared/components/inline-delete-confirmation/inline-delete-confirmation.component';

type FormType = {
  document: string | null;
};

@Component({
  selector: 'app-project-documents-menu',
  standalone: true,
  imports: [ SideMenuComponent,
    SideMenuTitleComponent,
    SideMenuContentComponent,
    SideMenuGroupComponent,
    SideMenuGroupTitleComponent,
    SideMenuGroupContentComponent,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslationPipeModule,
    MatAutocompleteModule,
    MatSelectModule,
    TextFieldModule,
    MatButton,
    DeleteButtonComponent,
    InlineConfirmationTriggerDirective,
    InlineDeleteConfirmationComponent ],
  providers: [
    provideTranslationScope("feature/project-customer-documents-menu")
  ],
  templateUrl: './project-documents-menu.component.html',
  styleUrl: './project-documents-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectDocumentsMenuComponent {
  private readonly _projectCustomerDocumentService: ProjectCustomerDocumentService = inject(ProjectCustomerDocumentService);

  private readonly _formGroupDirective: Signal<FormGroupDirective> = viewChild.required(FormGroupDirective);

  private readonly _environmentInjector: EnvironmentInjector = inject(EnvironmentInjector);

  private readonly _translationService: TranslationService = inject(TranslationService);

  private readonly _formBuilder: FormBuilder = inject(FormBuilder);

  public readonly isExpanded: ModelSignal<boolean> = model(false);

  @Output() public readonly add: EventEmitter<ProjectCustomerDocumentId> = new EventEmitter<ProjectCustomerDocumentId>();

  @Output() public readonly delete: EventEmitter<ProjectCustomerDocumentId> = new EventEmitter<ProjectCustomerDocumentId>();

  public projectCustomerDocuments: InputSignal<ProjectCustomerDocument[]> = input.required<ProjectCustomerDocument[]>();

  // eslint-disable-next-line @unicorn/no-useless-undefined
  public editDocument: InputSignal<ProjectCustomerDocument | undefined> = input<ProjectCustomerDocument | undefined>(undefined);

  // eslint-disable-next-line @unicorn/no-useless-undefined
  protected selectedDocument: WritableSignal<ProjectCustomerDocument | undefined> = signal<ProjectCustomerDocument | undefined>(undefined);

  protected selectedDocumentDuration: Signal<string> = toSignal(toObservable(this.selectedDocument)
    .pipe(
      map((value: ProjectCustomerDocument | undefined) => value?.duration),
      switchMap((duration: ProjectCustomerDocumentDuration | undefined) => {
        if (duration === undefined)
          return of("");

        if (isDuration(duration))
          return getLocalizedDuration(duration, this._environmentInjector);

        const translationScope: string = "feature/project-customer-documents-overview";

        if (duration === PROJECT_CUSTOMER_DOCUMENT_SPECIAL_DURATIONS.projectEnd)
          return this._translationService.translateTokenByLocale$("label-duration-until-project-end", "Bis Projektende", translationScope);


        return this._translationService.translateTokenByLocale$("label-duration-unlimited", "Unbegrenzt", translationScope);
      })
    ), { initialValue: "" });

  protected form: FormOf<FormType> = this._formBuilder.group({
    // eslint-disable-next-line @tseslint/unbound-method
    document: this._formBuilder.control<string | null>("", { validators: [ Validators.required ] })
  });

  private _allCustomerDocumentTypes: Signal<ProjectCustomerDocument[]> = toSignal(this._projectCustomerDocumentService.allDocumentTypes$, { initialValue: [] });

  protected selectableCustomerDocuments: Signal<ProjectCustomerDocument[]> = toSignal(
    this.form.controls.document.valueChanges
      .pipe(
        startWith(this.form.controls.document.value),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((searchTerm: string | null | ProjectCustomerDocument) => this._projectCustomerDocumentService.allDocumentTypes$
          .pipe(
            map((allDocuments: ProjectCustomerDocument[]) => allDocuments
              .filter((customerDocument: ProjectCustomerDocument) => {
                const isUnused: boolean = !this.projectCustomerDocuments()
                  .some((projectDocument: ProjectCustomerDocument) => projectDocument.id === customerDocument.id);
                const isTermMatchOrNoTerm: boolean = isString(searchTerm) ? searchTerm === this.selectedDocument()?.id || matchesTerm(customerDocument.name ?? "", searchTerm) : true;

                return isUnused && isTermMatchOrNoTerm;
              }))
          ))
      )
    , { initialValue: [] }
  );

  constructor () {
    toObservable(this.editDocument)
      .pipe(
        tap((editDocument: ProjectCustomerDocument | undefined) => {
          editDocument ? this.form.controls.document.disable() : this.form.controls.document.enable();
        }),
        takeUntilDestroyed()
      )
      .subscribe((value: ProjectCustomerDocument | undefined) => {
        if (value) {
          this.form.controls.document.setValue(value.id, { emitEvent: true, emitModelToViewChange: true });
          this.selectDocument(value.id);
        } else {
          this.selectedDocument.set(undefined);
          this._formGroupDirective().resetForm();
        }
      });
    toObservable(this.isExpanded)
      .pipe(
        filter((isExpanded: boolean) => !isExpanded)
      )
      .subscribe(() => {
        if (this.editDocument())
          this.selectedDocument.set(this.editDocument());
        else {
          this.selectedDocument.set(undefined);
          this._formGroupDirective().resetForm();
        }
      });
  }

  protected formatDocumentForTrigger = (customerDocumentId: string | null): string => {
    if (isNull(customerDocumentId)) return "";

    const matchingDocument: ProjectCustomerDocument | undefined = this._allCustomerDocumentTypes()
      .find((projectCustomerDocument: ProjectCustomerDocument) => projectCustomerDocument.id === customerDocumentId);

    return matchingDocument?.name ?? "";
  };


  protected selectDocument (selectedDocumentId: string): void {
    const matchingDocument: ProjectCustomerDocument | undefined = this._allCustomerDocumentTypes()
      .find((projectCustomerDocument: ProjectCustomerDocument) => projectCustomerDocument.id === selectedDocumentId);

    this.selectedDocument.set(matchingDocument);
  }

  protected _delete (): void {
    const editDocument: ProjectCustomerDocument | undefined = this.editDocument();

    if (editDocument)
      this[ "delete" ].emit({ id: editDocument.id });
  }

  protected cancel (): void {
    this.isExpanded.set(false);
  }

  protected submit (): void {
    const selectedDocument: ProjectCustomerDocument | undefined = this.selectedDocument();

    if (selectedDocument)
      this.add.emit({ id: selectedDocument.id });
  }
}

